import React from 'react';
import SEO from '../components/SEO';

const TermsConditions = () => {
  return (
    <>
      <SEO 
        title="Terms & Conditions | Trademark Supremacy"
        description="Trademark Supremacy Terms and Conditions. Read our terms of service for using our trademark registration services."
        keywords="terms and conditions, terms of service, legal terms"
        canonical="/terms-conditions"
      />
      <div className="min-h-screen py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl md:text-5xl font-bold text-center mb-8">Terms & Conditions</h1>
          <p className="text-gray-600 text-center mb-12">Last Updated: November 2024</p>
          
          <article className="prose prose-lg max-w-none bg-white p-8 rounded-lg shadow-sm">
            <p className="lead">
              The use of Trademark Supremacy's website (the "Website") constitutes your assent to and acceptance of these terms and conditions in full as an agreement between you and Trademark Supremacy. Our Privacy Policy is also incorporated into this Agreement. Please do not use the Website if you do not agree to these terms.
            </p>
            
            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 my-6">
              <p className="text-sm font-semibold">
                IMPORTANT: THIS AGREEMENT CONTAINS BINDING ARBITRATION PROVISIONS AND A CLASS ACTION WAIVER.
              </p>
            </div>

            <h2>1. Acceptance of Terms</h2>
            <p>By using this Website, you agree that you:</p>
            <ul>
              <li>Are at least 18 years old</li>
              <li>Will comply with all applicable laws and regulations</li>
              <li>Will provide accurate information when creating an account</li>
              <li>Are responsible for maintaining the confidentiality of your account</li>
              <li>Will not use the Website for any unlawful purpose</li>
              <li>Grant us the right to remove any content and terminate your account with or without notice</li>
            </ul>

            <h2>2. User Content</h2>
            <p>
              Any material you provide to us ("User Generated Content" or "UGC") may be published with our permission. You retain ownership of your content but grant us a non-exclusive, worldwide, royalty-free license to use, reproduce, and display your content.
            </p>
            <p>You agree that your content will not:</p>
            <ul>
              <li>Infringe on others' intellectual property rights</li>
              <li>Violate any laws or regulations</li>
              <li>Be defamatory, obscene, or harassing</li>
              <li>Contain harmful or malicious code</li>
              <li>Impersonate any person or entity</li>
            </ul>

            <h2>3. Prohibited Conduct</h2>
            <p>You may not:</p>
            <ul>
              <li>Use automated systems to access the Website without permission</li>
              <li>Attempt to gain unauthorized access to our systems</li>
              <li>Interfere with the proper functioning of the Website</li>
              <li>Send spam or unsolicited communications</li>
              <li>Violate any applicable laws or regulations</li>
            </ul>

            <h2>4. Our Services</h2>
            
            <h3>Trademark Services</h3>
            <p>
              We provide trademark registration and related services. Please note that we do not personalize forms, instructions, or recommendations to meet your unique requirements. We recommend consulting with a licensed attorney for specific legal advice.
            </p>

            <h3>Search Services</h3>
            <p>
              We use commercially reasonable methods to identify potentially conflicting marks. However, we cannot guarantee that your mark will be approved by the USPTO. The USPTO may reject applications for various reasons, and automated searches may not identify all potential conflicts.
            </p>

            <h3>Calendar Reminders</h3>
            <p>
              We may send deadline reminders related to your trademark. These are provided for informational purposes only and may not apply to your specific circumstances. Please consult with an attorney regarding deadlines applicable to your case.
            </p>

            <h2>5. Payment and Fees</h2>
            <p>
              We retain your credit card information to process government filing fees associated with your order, only after you approve the application and are informed of the costs. All fees are non-refundable unless otherwise specified in our Refund Policy.
            </p>

            <h2>6. Refund Policy</h2>
            <p>
              We are committed to 100% customer satisfaction. If you believe we have made an error, we will correct it at no additional cost. However:
            </p>
            <ul>
              <li>Trademark Supremacy service fees are non-refundable</li>
              <li>Government filing fees are non-refundable</li>
              <li>Refund requests must be submitted before the initial trademark search is conducted</li>
              <li>USPTO delays or denials for reasons beyond our control do not qualify for refunds</li>
            </ul>

            <h2>7. Intellectual Property</h2>
            <p>
              All content on the Website, including text, graphics, logos, images, and software, is owned by Trademark Supremacy or our licensors and is protected by copyright and trademark laws. You may not use, copy, or distribute any content without our written permission.
            </p>

            <h2>8. Disclaimers</h2>
            <p>
              THE WEBSITE AND SERVICES ARE PROVIDED \"AS IS\" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED. We do not warrant that:
            </p>
            <ul>
              <li>The Website will be available at all times or error-free</li>
              <li>The services will meet your specific requirements</li>
              <li>Any content is accurate or reliable</li>
              <li>Defects will be corrected</li>
            </ul>

            <h2>9. Limitation of Liability</h2>
            <p>
              TO THE MAXIMUM EXTENT PERMITTED BY LAW, WE SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING BUT NOT LIMITED TO:
            </p>
            <ul>
              <li>Loss of profits or revenue</li>
              <li>Loss of data or information</li>
              <li>Business interruption</li>
              <li>Loss of goodwill</li>
            </ul>

            <h2>10. Indemnification</h2>
            <p>
              You agree to defend, indemnify, and hold harmless Trademark Supremacy, our officers, directors, employees, and agents from any claims, damages, obligations, losses, liabilities, costs, or expenses arising from:
            </p>
            <ul>
              <li>Your use of the Website</li>
              <li>Your breach of these Terms</li>
              <li>Your violation of any third-party rights</li>
              <li>Your user-generated content</li>
            </ul>

            <h2>11. Dispute Resolution and Arbitration</h2>
            
            <h3>Binding Arbitration</h3>
            <p>
              Any dispute, claim, or controversy arising out of or relating to these Terms or the use of our services shall be resolved by binding arbitration administered by the American Arbitration Association (AAA) under its Consumer Arbitration Rules.
            </p>

            <h3>Class Action Waiver</h3>
            <p>
              YOU AND TRADEMARK SUPREMACY AGREE THAT EACH MAY BRING CLAIMS AGAINST THE OTHER ONLY IN YOUR OR ITS INDIVIDUAL CAPACITY AND NOT AS A PLAINTIFF OR CLASS MEMBER IN ANY CLASS ACTION OR REPRESENTATIVE PROCEEDING.
            </p>

            <h3>Opt-Out Right</h3>
            <p>
              You have 30 days from accepting these Terms to opt-out of arbitration by sending written notice to info@trademarksupremacy.com with your name, address, and a clear statement that you wish to opt-out.
            </p>

            <h2>12. Governing Law</h2>
            <p>
              These Terms shall be governed by the laws of the State of Texas, without regard to its conflict of law provisions. The Federal Arbitration Act governs the interpretation and enforcement of the arbitration provision.
            </p>

            <h2>13. Changes to Terms</h2>
            <p>
              We reserve the right to modify these Terms at any time. Changes will be effective upon posting to the Website. Your continued use of the Website after changes are posted constitutes acceptance of the modified Terms.
            </p>

            <h2>14. Termination</h2>
            <p>
              We may terminate or suspend your access to the Website at any time, with or without cause or notice. Upon termination, all rights granted to you under these Terms will immediately cease.
            </p>

            <h2>15. Miscellaneous</h2>
            
            <h3>Entire Agreement</h3>
            <p>
              These Terms, together with our Privacy Policy, constitute the entire agreement between you and Trademark Supremacy regarding use of the Website.
            </p>

            <h3>Severability</h3>
            <p>
              If any provision of these Terms is found to be invalid or unenforceable, the remaining provisions shall remain in full force and effect.
            </p>

            <h3>Waiver</h3>
            <p>
              Our failure to enforce any right or provision of these Terms shall not constitute a waiver of such right or provision.
            </p>

            <h3>Assignment</h3>
            <p>
              You may not assign or transfer these Terms without our written consent. We may assign our rights and obligations without restriction.
            </p>

            <h2>16. Contact Information</h2>
            <p>
              If you have questions about these Terms, please contact us:
            </p>
            <ul>
              <li><strong>Email:</strong> info@trademarksupremacy.com</li>
              <li><strong>Phone:</strong> +1 (415) 854 3407</li>
            </ul>

            <p className="mt-8 text-sm text-gray-600">
              By using TrademarkPro's services, you acknowledge that you have read, understood, and agree to be bound by these Terms and Conditions.
            </p>
          </article>
        </div>
      </div>
    </>
  );
};

export default TermsConditions;
